import React, {useState} from "react";
import {Alert, Button, Card, Col, message, Progress, Row, Space, Tooltip, Upload} from "antd";
import {SelectHierarchy} from "./SelectHierarchy";
import {HierarchyModel} from "../../services/HierarchiesService";
import {UploadProps} from "antd/es/upload";
import {API_URL} from "../../constants";
import {authHeader, empty, UpdateJobProgress, download} from "../../Utilities";
import {HttpRequestHeader} from "antd/es/upload/interface";
import {axiosUploadRequest} from "../../UtilitiesTs";
import axios from "axios";

export function ConfigureHierarchyScope({workspaceId}: { workspaceId: string }) {
    const [selectedHierarchy, setSelectedHierarchy] = useState<HierarchyModel | null>(null);
    return (
        <Row gutter={16}>
            <Col span={8}>
                <SelectHierarchy
                    workspaceId={workspaceId}
                    onSelect={setSelectedHierarchy}
                />
            </Col>
            <Col span={16}>
                {selectedHierarchy && (
                    <Config
                        key={selectedHierarchy.id}
                        workspaceId={workspaceId}
                        hierarchy={selectedHierarchy}
                    />
                )}
            </Col>
        </Row>
    );
}

export function Config({workspaceId, hierarchy}: { hierarchy: HierarchyModel, workspaceId: string }) {
    return (
        <Card title={"Configure hierarchy scope"}>
            <Row>
                <Space>
                    <UploadExcel
                        hierarchyId={hierarchy.id}
                        onOk={empty}
                    />
                    <DownloadExcel 
                        hierarchyId={hierarchy.id}
                    />
                </Space>
            </Row>
        </Card>
    );
}

function UploadExcel({hierarchyId, onOk}: {
    hierarchyId: string,
    onOk: () => any,
}) {
    const [progress, setProgress] = useState<any>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);

    const uploadProps: Partial<UploadProps> = {
        accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        name: "file",
        action: `${API_URL}/hierarchies/${hierarchyId}/pages_in_scope/import_from_excel`,
        headers: authHeader() as HttpRequestHeader,
        data: {},
        onChange(info) {
            if (info.file.status === "done") {
                const updateProgress = new UpdateJobProgress().withDefaultUpdateProgress(setProgress);
                setError(undefined);
                updateProgress.do(
                    `${API_URL}/hierarchies/${hierarchyId}/pages_in_scope/import_from_excel/${info.file.response}/progress`
                ).then(_ => {
                    message.success("Operation completed successfully");
                    setProgress({status: "Completed", percent: 100});
                    onOk();
                }).catch(error => {
                    console.error(error);
                    setError(error.toString());

                    const msg = error.response?.data?.error ?? error.response?.data?.message ?? error.message ?? String(error);

                    message.error(msg);
                });
            } else if (info.file.status === "error") {
                message.error("Failed to upload file!");
            }
        },
        customRequest: axiosUploadRequest,
    };

    return (
        <>
            <Tooltip title="Upload an Excel file to set which pages are in scope">
                <Upload {...uploadProps} showUploadList={true}>
                    <Button type={"primary"}>Upload excel</Button>
                </Upload>
            </Tooltip>
            {progress && (
                <div>
                    {progress.status}
                    <Progress percent={progress.percent}/>
                </div>
            )}
            {error != null && (
                <Alert message={error} type={"error"}/>
            )}
        </>
    );
}

function DownloadExcel({hierarchyId}: { hierarchyId: string }) {
    const [progress, setProgress] = useState<any>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const runExportAndDownload = (endpointPath: string, filename: string) => {
        setError(undefined);
        setLoading(true);

        axios.get(
            `${API_URL}/hierarchies/${hierarchyId}/pages_in_scope/${endpointPath}`,
            {
                headers: authHeader(),
            }).then((result) => {
            const taskId = result.data;
            const updateProgress = new UpdateJobProgress().withDefaultUpdateProgress(setProgress);
            updateProgress.do(
                `${API_URL}/hierarchies/${hierarchyId}/pages_in_scope/${endpointPath}/${taskId}/progress`
            ).then(() => {
                axios.get(`${API_URL}/hierarchies/${hierarchyId}/pages_in_scope/${endpointPath}/${taskId}/download`,
                {
                    headers: authHeader(),
                    responseType: "blob"
                }).then((result) => {
                    const blob = new window.Blob([result.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    });
                    download(blob, filename);
                    message.success("Excel file downloaded successfully");
                }).catch((err) => {
                    setError(err.message);
                });
            });
        }).finally(() => {
            setLoading(false);
            setError(undefined);
        });
    };

    const onDownloadCurrentScope = () =>
        runExportAndDownload("export_current_scope", "current_scope.xlsx");
    const onDownloadPossibleScope = () =>
        runExportAndDownload("export_possible_scope", "possible_scopes.xlsx");

    return (
        <Row>
            <Space>
                <Tooltip title="Download the current hierarchy scope as Excel">
                    <span style={{display: "inline-block"}}>
                        <Button
                            type="primary"
                            onClick={onDownloadCurrentScope}
                            loading={loading}
                            disabled={loading}
                        >
                            Download current scope
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Download all possible pages in scope as Excel">
                    <span style={{display: "inline-block"}}>
                        <Button
                            type="primary"
                            onClick={onDownloadPossibleScope}
                            loading={loading}
                            disabled={loading}
                        >
                            Download possible scope
                        </Button>
                    </span>
                </Tooltip>
            </Space>
            {progress != null && (
                <div>
                    {progress.status}
                    <Progress percent={progress.percent}/>
                </div>
            )}
            {error != null && (
                <Alert message={error} type={"error"}/>
            )}
        </Row>
    );
}